/**
 * Making an agent, or changing one.
 *
 * Two decisions are load-bearing — which model answers, and which knowledge
 * graphs it may read — so those are the whole first screen. Everything else has
 * a defensible default and lives behind "More settings", because a create dialog
 * with nine fields is one people abandon.
 *
 * The graph picker is checkboxes rather than a multi-select: the list is short,
 * the choice is the point of the feature, and a native multi-select is the
 * control everyone mis-clicks.
 */

import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { ErrorNotice, Loading, Modal } from '@/shared/components'
import { ModelPicker, type PickerGroup, providerName } from '@/shared/components/ModelPicker'
import { useToast } from '@/shared/components/toast'
import { Chip, Field } from '@/features/brain/components'
import {
  useAgentOptions,
  useConnectionsModels,
  useCreateAgent,
  useUpdateAgent,
} from './hooks'
import type { Agent, AgentDraft, Grounding, WebDepth } from './types'

/** Something to pick from without opening a picker. */
const AVATARS = ['◈', '📘', '🧭', '🔍', '📊', '🧪', '⚖️', '🛟', '🗂️', '🛰️', '🧩', '💡']

export function AgentDialog({
  agent,
  onClose,
  onSaved,
}: {
  agent?: Agent
  onClose: () => void
  onSaved: (agent: Agent) => void
}) {
  const options = useAgentOptions()
  const create = useCreateAgent()
  const update = useUpdateAgent(agent?.assistant_id ?? '')
  const toast = useToast()
  const location = useLocation()

  const [name, setName] = useState(agent?.name ?? '')
  const [description, setDescription] = useState(agent?.description ?? '')
  const [avatar, setAvatar] = useState(agent?.avatar ?? '◈')
  const [model, setModel] = useState(agent?.model ?? '')
  const [graphIds, setGraphIds] = useState<string[]>(agent?.graph_ids ?? [])
  const [instructions, setInstructions] = useState(agent?.instructions ?? '')
  const [grounding, setGrounding] = useState<Grounding>(agent?.grounding ?? 'strict')
  const [temperature, setTemperature] = useState(agent?.temperature ?? 0.2)
  const [maxTokens, setMaxTokens] = useState(agent?.max_tokens ?? 1200)
  const [maxRecords, setMaxRecords] = useState(agent?.max_records ?? 12)
  const [more, setMore] = useState(false)

  // Which key answers. '' is the deployment's own, which is what an agent made
  // before connections existed uses and what a new one starts on.
  const [llmConnectionId, setLlmConnectionId] = useState(agent?.llm_connection_id ?? '')
  const [webSearch, setWebSearch] = useState(agent?.web_search ?? false)
  const [searchConnectionId, setSearchConnectionId] = useState(
    agent?.search_connection_id ?? '',
  )
  const [webResults, setWebResults] = useState(agent?.web_results ?? 4)
  const [webDepth, setWebDepth] = useState<WebDepth>(agent?.web_depth ?? 'basic')

  const graphs = options.data?.graphs ?? []
  const llmConnections = options.data?.llm_connections ?? []
  const searchConnections = options.data?.search_connections ?? []
  const serverSearchKey = options.data?.web_search?.server_key ?? false

  // Every source a model can come from, the way the Chat page lists them: the
  // service keys as one group, then each connection with its own list, read
  // with its own key. All fetched at once so the picker opens complete.
  const connectionModels = useConnectionsModels(llmConnections.map((c) => c.connection_id))
  const groups = useMemo(() => {
    const out: PickerGroup[] = []
    if (options.data) {
      out.push({
        key: 'service',
        label: "This server's key",
        sub: options.data.providers?.length
          ? `${options.data.providers.map(providerName).join(' + ')} — may fall back to another model`
          : null,
        models: options.data.models,
        error: options.data.models_configured
          ? options.data.models_error
          : 'No model key is set on the API.',
      })
    }
    llmConnections.forEach((c, i) => {
      const q = connectionModels[i]
      out.push({
        key: c.connection_id,
        label: c.label,
        sub: `your ${providerName(c.connector_id)} key — pinned, no fallback${
          c.status === 'active' ? '' : ` · ${c.status}`
        }`,
        models: q?.data?.models ?? [],
        error: q?.data?.error ?? (q?.error ? String((q.error as Error).message) : null),
        loading: q?.isLoading,
      })
    })
    return out
  }, [options.data, llmConnections, connectionModels])

  // '' is the service key; the picker calls that group 'service'.
  const source = llmConnectionId || 'service'
  const current = groups.find((g) => g.key === source)
  const models = current?.models ?? []
  const modelsError = current?.error ?? null
  const modelsLoading = Boolean(current?.loading)

  // Open on something that answers rather than making the first act of
  // creating an agent a decision about a list not yet seen. An existing
  // agent's choice is kept while it still exists somewhere; one that has
  // vanished from its key (a retired model, a deleted connection) is replaced
  // rather than saved again, because saving it would make an agent that fails
  // on its first question with "model does not exist".
  useEffect(() => {
    if (modelsLoading) return
    if (model && models.some((m) => m.model === model)) return
    const first =
      groups.find((g) => g.key === source && g.models.length && !g.loading) ??
      groups.find((g) => g.models.length && !g.loading)
    if (!first) return
    setLlmConnectionId(first.key === 'service' ? '' : first.key)
    setModel(first.models[0].model)
  }, [groups, source, model, models, modelsLoading])

  // One graph and nothing to choose is not a choice; pre-tick it.
  useEffect(() => {
    if (!agent && graphIds.length === 0 && graphs.length === 1) {
      setGraphIds([graphs[0].graph_id])
    }
  }, [agent, graphs, graphIds.length])

  const chosenModel = useMemo(
    () => models.find((m) => m.model === model),
    [models, model],
  )

  const pending = create.isPending || update.isPending
  const valid = name.trim().length > 0 && model.length > 0

  // Web search turned on with nothing to serve it would save cleanly and then
  // fail on every question, so it is said here instead.
  const searchUnserved =
    webSearch && !searchConnectionId && !serverSearchKey

  async function save() {
    const draft: AgentDraft = {
      name: name.trim(),
      model,
      graph_ids: graphIds,
      description: description.trim() || null,
      avatar,
      instructions: instructions.trim() || null,
      grounding,
      temperature,
      max_tokens: maxTokens,
      max_records: maxRecords,
      // '' is "the deployment's key", which the server reads as null. Sent
      // explicitly so clearing the picker clears the stored connection rather
      // than leaving the old one in place.
      llm_connection_id: llmConnectionId || null,
      web_search: webSearch,
      search_connection_id: searchConnectionId || null,
      web_results: webResults,
      web_depth: webDepth,
    }
    try {
      const saved = agent
        ? await update.mutateAsync(draft)
        : await create.mutateAsync(draft)
      toast.success(agent ? 'Agent updated' : 'Agent created', saved.name)
      onSaved(saved)
    } catch (error) {
      toast.error(error)
    }
  }

  return (
    <Modal
      wide
      title={agent ? `Configure ${agent.name}` : 'New agent'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={save} disabled={!valid || pending}>
            {pending ? 'Saving…' : agent ? 'Save changes' : 'Create agent'}
          </button>
        </>
      }
    >
      {options.isLoading && <Loading label="Loading models and graphs…" />}
      {options.error && <ErrorNotice error={options.error} onRetry={options.refetch} />}

      {options.data && (
        <div className="stack" style={{ gap: 14 }}>
          {/* ------------------------------------------------------ identity */}
          <div className="ag-identity">
            <div className="ag-avatarpick">
              <span className="ag-avatar ag-avatar-lg" aria-hidden="true">
                {avatar}
              </span>
              <div className="ag-avatarrow">
                {AVATARS.map((glyph) => (
                  <button
                    key={glyph}
                    type="button"
                    className="ag-avatarbtn"
                    data-on={glyph === avatar}
                    aria-label={`Use ${glyph} as the icon`}
                    onClick={() => setAvatar(glyph)}
                  >
                    {glyph}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ flex: 1 }}>
              <Field label="Name" help="What you will call it in the list.">
                <input
                  className="input"
                  value={name}
                  autoFocus
                  placeholder="Wiki analyst"
                  onChange={(e) => setName(e.target.value)}
                />
              </Field>
              <Field label="Description" help="Optional. What it is for.">
                <input
                  className="input"
                  value={description}
                  placeholder="Answers questions about spaces, pages and who wrote them"
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Field>
            </div>
          </div>

          {/* --------------------------------------------------------- model */}
          <Field
            label="Model"
            help={
              llmConnectionId
                ? 'On your own key, and pinned: there is no fallback, so the answer comes from this model or not at all.'
                : options.data.models_configured
                  ? "On this server's key. Which model writes the answer — the evidence is the same either way. Need one that is not listed? Connect Groq or Hugging Face with your own key from the list."
                  : 'No model key is configured, so an agent can be made but not asked. Connect a key of your own from the list.'
            }
          >
            <ModelPicker
              block
              drop="down"
              groups={groups}
              source={source}
              model={model}
              onPick={(key, id) => {
                setLlmConnectionId(key === 'service' ? '' : key)
                setModel(id)
              }}
              connectors={options.data.llm_connectors ?? []}
              returnTo={location.pathname}
              placeholder={modelsLoading ? 'Reading models…' : 'Choose a model'}
            />
          </Field>
          {chosenModel && (
            <p className="small faint">
              {chosenModel.summary}
              {chosenModel.served_by?.length
                ? ` Served by ${chosenModel.served_by.slice(0, 3).join(', ')}${
                    chosenModel.served_by.length > 3 ? ' and others' : ''
                  }.`
                : ''}
            </p>
          )}
          {modelsError && !models.length && (
            <div className="callout callout-warn">
              <p className="small" style={{ margin: 0 }}>
                {modelsError}
              </p>
            </div>
          )}

          {/* -------------------------------------------------------- graphs */}
          <Field
            label="Knowledge graphs"
            help={
              graphIds.length
                ? 'What it reads, and everything a grounded answer has to come from. Graphs can also be chosen per conversation from the + menu.'
                : 'Optional. Without one it answers like the Chat page — from the model, the web and attached files — and says so on every answer. Add a graph here or from the + menu in a conversation.'
            }
          >
            {graphs.length === 0 ? (
              <p className="small muted">
                No graphs yet. The agent will answer openly; create one under Knowledge graph
                and it can be attached here or from the + menu later.
              </p>
            ) : (
              <div className="ag-graphpick">
                {graphs.map((graph) => {
                  const on = graphIds.includes(graph.graph_id)
                  return (
                    <label key={graph.graph_id} className="ag-graphrow" data-on={on}>
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() =>
                          setGraphIds((current) =>
                            on
                              ? current.filter((id) => id !== graph.graph_id)
                              : [...current, graph.graph_id],
                          )
                        }
                      />
                      <span className="ag-graphname">{graph.name}</span>
                      {graph.description && (
                        <span className="small faint truncate">{graph.description}</span>
                      )}
                      <span className="cp-spacer" />
                      <Chip>{graph.status}</Chip>
                    </label>
                  )
                })}
              </div>
            )}
          </Field>

          {/* ---------------------------------------------------- web search */}
          <Field
            label="Web search"
            help="Off by default, and that default is the point: an agent that only cites your own systems is one whose answers you can check against them."
          >
            <label className="ag-graphrow" data-on={webSearch}>
              <input
                type="checkbox"
                checked={webSearch}
                onChange={() => setWebSearch((v) => !v)}
              />
              <span className="ag-graphname">Let this agent search the web</span>
              <span className="small faint">
                Results arrive numbered and citable, marked as web.
              </span>
            </label>
          </Field>

          {webSearch && (
            <div className="stack" style={{ gap: 12 }}>
              <Field
                label="Search through"
                help={
                  serverSearchKey
                    ? 'Leave unset to use this server’s own Tavily key.'
                    : 'This server has no search key, so an agent needs a connection of its own.'
                }
              >
                <select
                  className="select"
                  value={searchConnectionId}
                  onChange={(e) => setSearchConnectionId(e.target.value)}
                >
                  <option value="">
                    {serverSearchKey
                      ? "This server's Tavily key"
                      : 'Nothing — pick a connection'}
                  </option>
                  {searchConnections.map((c) => (
                    <option key={c.connection_id} value={c.connection_id}>
                      {c.label}
                      {c.status === 'active' ? '' : ` — ${c.status}`}
                    </option>
                  ))}
                </select>
              </Field>

              {searchUnserved && (
                <div className="callout callout-warn">
                  <p className="small" style={{ margin: 0 }}>
                    Nothing serves web search yet. Connect Tavily under Connections and
                    pick it here, or set TAVILY_API_KEY on the API service — otherwise
                    this agent will say the search did not run rather than searching.
                  </p>
                </div>
              )}

              <div className="row" style={{ gap: 14, alignItems: 'flex-start' }}>
                <Field
                  label="Pages per question"
                  help="Web results and records share one context window."
                >
                  <input
                    className="input"
                    type="number"
                    min={1}
                    max={10}
                    value={webResults}
                    onChange={(e) => setWebResults(Number(e.target.value))}
                  />
                </Field>
                <Field label="Depth" help="Advanced reads further into each page, and costs more credits.">
                  <select
                    className="select"
                    value={webDepth}
                    onChange={(e) => setWebDepth(e.target.value as WebDepth)}
                  >
                    <option value="basic">Basic</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </Field>
              </div>
            </div>
          )}

          {/* ------------------------------------------------- more settings */}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setMore((v) => !v)}
            style={{ alignSelf: 'flex-start' }}
          >
            {more ? 'Fewer settings' : 'More settings'}
          </button>

          {more && (
            <div className="stack" style={{ gap: 12 }}>
              <Field
                label="Instructions"
                help="Added to the answering rules, never instead of them. Tone, format, what to lead with."
              >
                <textarea
                  className="input mono"
                  rows={4}
                  value={instructions}
                  placeholder="Answer in bullet points. Always name the space a page belongs to."
                  onChange={(e) => setInstructions(e.target.value)}
                />
              </Field>

              <Field
                label="When the graph has no answer"
                help="Strict is the default, and the reason an answer from this agent is worth trusting."
              >
                <div className="ag-radios">
                  <label data-on={grounding === 'strict'}>
                    <input
                      type="radio"
                      checked={grounding === 'strict'}
                      onChange={() => setGrounding('strict')}
                    />
                    <span>
                      <strong>Refuse</strong>
                      <span className="small faint">
                        Say so plainly. Never answer from general knowledge.
                      </span>
                    </span>
                  </label>
                  <label data-on={grounding === 'assisted'}>
                    <input
                      type="radio"
                      checked={grounding === 'assisted'}
                      onChange={() => setGrounding('assisted')}
                    />
                    <span>
                      <strong>Answer, and mark it</strong>
                      <span className="small faint">
                        May add general knowledge, prefixed “Beyond the graph:”.
                      </span>
                    </span>
                  </label>
                </div>
              </Field>

              <div className="row" style={{ gap: 14, alignItems: 'flex-start' }}>
                <Field
                  label={`Creativity — ${temperature.toFixed(1)}`}
                  help="0 is the same answer every time. Higher varies the wording, not the facts."
                >
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.1}
                    value={temperature}
                    onChange={(e) => setTemperature(Number(e.target.value))}
                  />
                </Field>
                <Field label="Answer length" help="Maximum tokens in a reply.">
                  <input
                    className="input"
                    type="number"
                    min={128}
                    max={8000}
                    step={100}
                    value={maxTokens}
                    onChange={(e) => setMaxTokens(Number(e.target.value))}
                  />
                </Field>
                <Field label="Records per question" help="How much evidence to gather.">
                  <input
                    className="input"
                    type="number"
                    min={1}
                    max={40}
                    value={maxRecords}
                    onChange={(e) => setMaxRecords(Number(e.target.value))}
                  />
                </Field>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}
